// One-shot sessionStorage handoff for the post-acceptance welcome tour.
//
// WelcomePage.js (src/components/features/invitations/WelcomePage.js) calls
// requestWelcomeTour() immediately before navigating to "/" on successful
// platform-invitation acceptance. WelcomeTourLauncher.js, mounted inside
// TourProvider in src/App.js, calls consumeWelcomeTourRequest() once the
// authenticated shell boots and launches WELCOME_TOUR_ID from there.
//
// Why a browser API and not a shared import: useTour() throws outside a
// TourProvider, and TourProvider mounts only inside the authenticated app
// shell. /welcome must stay OUTSIDE that shell, because entering it triggers
// ensureUserAccount, which provisions the customer on the default billing
// tier — the exact bug the platform-invitation flow exists to prevent. So the
// handoff crosses the unauthenticated/authenticated boundary through
// sessionStorage, the same one-shot-signal pattern already used by
// MessagingOnboardingBanner.js and useTourNudge.js's snooze keys.
//
// This module has ZERO imports on purpose. WelcomePage.js can depend on it
// without pulling in TourContext, driver.js, or any authenticated-shell
// state, which is the one thing that page must never do (see its own header
// comment and tests/welcomePage.test.js's ensureUserAccount guard).
//
// Both functions are total: they never throw. sessionStorage is unavailable in
// some private-browsing modes and over quota in others, and a failed handoff
// is a soft failure — the workspace is already claimed by the time the flag is
// written, so the worst case is that the tour does not auto-launch.

/** tourRegistry key for the tour this handoff launches (tourRegistry.js). */
export const WELCOME_TOUR_ID = 'welcome';

/** sessionStorage key carrying the request across the navigation to "/". */
export const WELCOME_TOUR_LAUNCH_FLAG = 'keyflow:launch-welcome-tour';

/**
 * Record that the welcome tour should launch on the next authenticated boot.
 *
 * @returns {boolean} true when the request was stored, false when storage was
 *   unavailable and the tour will therefore not auto-launch.
 */
export function requestWelcomeTour() {
    try {
        if (typeof sessionStorage === 'undefined') return false;
        sessionStorage.setItem(WELCOME_TOUR_LAUNCH_FLAG, '1');
        return true;
    } catch (_e) {
        return false;
    }
}

/**
 * Read the request and clear it in the same call, so it can only ever be
 * honored once. Clearing BEFORE the caller launches anything is what makes a
 * refresh mid-tour — or a second mount for any other reason — a no-op.
 *
 * @returns {boolean} true when a request was pending, false otherwise.
 */
export function consumeWelcomeTourRequest() {
    try {
        if (typeof sessionStorage === 'undefined') return false;
        if (sessionStorage.getItem(WELCOME_TOUR_LAUNCH_FLAG) !== '1') return false;
        sessionStorage.removeItem(WELCOME_TOUR_LAUNCH_FLAG);
        return true;
    } catch (_e) {
        return false;
    }
}
